/**
 * @fileoverview AR-670 — Ports (hexagonal architecture) for the Google
 * Classroom integration. Components depend ONLY on these interfaces (via the
 * hooks). The READ-ONLY backend (branch feat-classroom-link-only-courses)
 * fulfills everything except grade push (phase 4), which stays unavailable
 * behind CLASSROOM_CAPABILITIES (see ../provider.ts).
 */

import {
  ClassroomCourseCatalog,
  GradePushResult,
  ProjectLinkResult,
  ProjectLinkState,
  SchoolConnection,
} from './types'
import { LinkedCourseRow, StudentClassroomFiles, SubmissionsSyncSummary } from './phase3Types'

/**
 * Backend: /api/arc_mosaico/google_workspace_connection (PR #1384).
 * Connect is a 2-step OAuth flow: startConnect() redirects to Google; the
 * page comes back with ?code=... and calls completeConnect(code).
 */
export interface SchoolConnectionPort {
  getConnection(): Promise<SchoolConnection>
  /** Fetches the authorization URL and redirects the browser to Google. */
  startConnect(redirectUri: string): Promise<void>
  /** Exchanges the OAuth code returned on the callback. */
  completeConnect(code: string, redirectUri: string): Promise<SchoolConnection>
  disconnect(): Promise<void>
}

export interface CoursesPort {
  /** GET /google_workspace_connection/courses — local synced catalog
   *  (+linkedProject per course, syncedAt, connectionStatus). A connection in
   *  needs_reconnection serves the cache instead of failing with 422. */
  listCourses(): Promise<ClassroomCourseCatalog>
  /** GET /google_classroom_courses — the school's existing project↔course
   *  links for the manager table. Local data, no Google call. */
  listLinkedCourses(): Promise<LinkedCourseRow[]>
}

/** Backend: /api/arc_mosaico/projects/:id/google_classroom_course. */
export interface ProjectLinkPort {
  getLink(projectId: string): Promise<ProjectLinkState>
  /** Links an existing course AND runs the roster matching synchronously —
   *  the result carries the {matchedCount, unmatchedCount, unmatched} summary
   *  (roster null + rosterError when only the matching failed). */
  linkCourse(projectId: string, courseId: string): Promise<ProjectLinkResult>
  syncRoster(projectId: string): Promise<ProjectLinkResult>
  setSyncEnabled(projectId: string, enabled: boolean): Promise<ProjectLinkState>
  unlink(projectId: string): Promise<void>
}

/**
 * Read-only student/teacher data flows. There is no assignment creation
 * anymore: the teacher imports LEGACY submissions of the project, and the
 * student reads the files they posted on the course stream.
 */
export interface SubmissionSyncPort {
  /** POST /projects/:id/google_classroom_course/sync_submissions — teacher,
   *  on-demand import of every legacy assignment of the project (the cron
   *  does the same every 15 min). */
  syncSubmissions(projectId: string): Promise<SubmissionsSyncSummary[]>
  /** GET /api/student_tasks/:id/google_classroom_files — files the student
   *  posted on the course stream (announcements). Unavailable states come
   *  back as 200 {available:false, reason}, never as errors. */
  getStudentFiles(studentTaskId: string): Promise<StudentClassroomFiles>
}

/** Phase 4 (grade push) — NO backend yet. */
export interface GradePort {
  pushGrade(activityId: string, studentId: string, grade: number | null): Promise<GradePushResult>
}

/** Everything a screen may need, resolved once by the provider factory. */
export interface ClassroomAdapters {
  connection: SchoolConnectionPort
  courses: CoursesPort
  projectLink: ProjectLinkPort
  submissionSync: SubmissionSyncPort
  grade: GradePort
}

/** Which integration surfaces have real backend behind them. */
export interface ClassroomCapabilities {
  /** Manager "Linked courses" table — no backend endpoint yet. */
  managerCourseLinks: boolean
  /** Activity assignment flag + status (phase 3, PR #1387). */
  assignments: boolean
  /** Student add-from-Classroom + submissions sync (phase 3, PR #1387). */
  studentSync: boolean
  /** Assessment drawer grade push (phase 4 — no backend). */
  gradePush: boolean
}
